<template>
  <div class="flags-detailed-card">
    <div class="flags-detailed-card__groups">
      <div
          v-for="group in orderedGroups"
          :key="group.key"
          class="flags-detailed-card__group"
      >
        <div class="flags-detailed-card__group-header">
          <div class="flags-detailed-card__group-title-wrap">
            <div class="flags-detailed-card__group-title">
              <span
                  class="flags-detailed-card__mark"
                  :style="{ backgroundColor: group.color }"
              ></span>

              <span class="flags-detailed-card__group-title-text">
                {{ group.title }}
              </span>
            </div>

            <div class="flags-detailed-card__group-line"></div>
          </div>

          <span
              class="flags-detailed-card__group-total"
              :class="{
                'flags-detailed-card__group-total--underlined': group.total !== 0
              }"
          >
            {{ group.total }} чел.
          </span>
        </div>

        <div
            v-if="group.items.length"
            class="flags-detailed-card__group-items"
            :class="{ 'bigger-gap': !smallGap }"
        >
          <div
              v-for="item in group.items"
              :key="item.key"
              class="flags-detailed-card__row"
          >
            <span class="flags-detailed-card__row-label">
              {{ item.label }}
            </span>

            <span
                class="flags-detailed-card__row-change"
                :class="getChangeClass(item.change)"
            >
              {{ getChangeText(item.change) }}
            </span>

            <span
                class="flags-detailed-card__row-value"
                :class="{
                  'flags-detailed-card__row-value--underlined': item.value !== 0
                }"
            >
              {{ item.value }} чел.
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="flags-detailed-card__bar-block">
      <div
          class="flags-detailed-card__bar"
          :style="orderedBarGridStyle"
      >
        <div
            v-for="(item, index) in orderedBarSegments"
            :key="item.key"
            class="flags-detailed-card__bar-part"
            :style="getBarPartStyle(index)"
        ></div>
      </div>

      <div
          class="flags-detailed-card__bar-values"
          :style="orderedBarGridStyle"
      >
        <span
            v-for="item in orderedBarSegments"
            :key="item.key"
            class="flags-detailed-card__bar-value"
        >
          {{ item.value }}
        </span>
      </div>

    </div>
  </div>
</template>

<script>
import { computed } from 'vue'

const GREY_COLOR = '#D1D1D1'

const GROUP_ORDER = ['balanced', 'risks', 'moderate']
const BAR_ORDER = ['risks', 'moderate', 'balanced']

const DEFAULT_BAR_SEGMENTS = [
  {
    key: 'risks',
    value: 0,
    color: GREY_COLOR,
    ratio: 1,
  },
  {
    key: 'moderate',
    value: 0,
    color: GREY_COLOR,
    ratio: 1,
  },
  {
    key: 'balanced',
    value: 0,
    color: GREY_COLOR,
    ratio: 1,
  },
]

export default {
  name: 'FlagsDetailedCard',

  props: {
    groups: {
      type: Array,
      default: () => [],
    },

    segments: {
      type: Array,
      default: () => [],
    },

    smallGap: {
      type: Boolean,
      default: true,
    },
  },

  setup(props) {
    const toNumber = (value) => {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    }

    const sortByKeyOrder = (items, order) => {
      const orderMap = new Map(
          order.map((key, index) => [key, index])
      )

      return [...items].sort((a, b) => {
        const aOrder = orderMap.has(a.key) ? orderMap.get(a.key) : 999
        const bOrder = orderMap.has(b.key) ? orderMap.get(b.key) : 999

        return aOrder - bOrder
      })
    }

    const normalizedGroups = computed(() => {
      return (Array.isArray(props.groups) ? props.groups : []).map((group, groupIndex) => ({
        ...group,
        key: group?.key ?? `group-${groupIndex}`,
        total: toNumber(group?.total),
        change: toNumber(group?.change),
        color: group?.color || GREY_COLOR,
        items: Array.isArray(group?.items)
            ? group.items.map((item, itemIndex) => ({
              ...item,
              key: item?.key ?? `item-${groupIndex}-${itemIndex}`,
              value: toNumber(item?.value),
              change: toNumber(item?.change),
            }))
            : [],
      }))
    })

    const orderedGroups = computed(() => {
      return sortByKeyOrder(normalizedGroups.value, GROUP_ORDER)
    })

    const normalizedSegments = computed(() => {
      return (Array.isArray(props.segments) ? props.segments : []).map((item, index) => {
        const value = toNumber(item?.value)

        return {
          ...item,
          key: item?.key ?? `segment-${index}`,
          value,
          color: value === 0 ? GREY_COLOR : (item?.color || GREY_COLOR),
        }
      })
    })

    const segmentsTotal = computed(() => {
      return normalizedSegments.value.reduce((sum, item) => sum + item.value, 0)
    })

    const allSegmentsAreZero = computed(() => {
      return (
          normalizedSegments.value.length > 0 &&
          normalizedSegments.value.every((item) => item.value === 0)
      )
    })

    const hasChartData = computed(() => {
      const hasSegmentValues = normalizedSegments.value.some((item) => item.value > 0)
      const hasGroupValues = normalizedGroups.value.some((group) => {
        return group.total > 0 || group.items.some((item) => item.value > 0)
      })

      return hasSegmentValues || hasGroupValues
    })

    const barSegments = computed(() => {
      if (!normalizedSegments.value.length) {
        return DEFAULT_BAR_SEGMENTS
      }

      if (allSegmentsAreZero.value) {
        return normalizedSegments.value.map((item) => ({
          ...item,
          color: GREY_COLOR,
          ratio: 1,
        }))
      }

      if (!segmentsTotal.value) {
        return DEFAULT_BAR_SEGMENTS
      }

      return normalizedSegments.value
          .filter((item) => item.value > 0)
          .map((item) => ({
            ...item,
            ratio: item.value,
          }))
    })

    const orderedBarSegments = computed(() => {
      return sortByKeyOrder(barSegments.value, BAR_ORDER)
    })

    const orderedBarGridStyle = computed(() => {
      return {
        gridTemplateColumns: orderedBarSegments.value
            .map((item) => `${item.ratio}fr`)
            .join(' '),
      }
    })

    const getBarPartStyle = (index) => {
      const isFirst = index === 0
      const isLast = index === orderedBarSegments.value.length - 1
      const currentItem = orderedBarSegments.value[index]

      return {
        backgroundColor: currentItem.color,
        borderTopLeftRadius: isFirst ? '999px' : '0',
        borderBottomLeftRadius: isFirst ? '999px' : '0',
        borderTopRightRadius: isLast ? '999px' : '0',
        borderBottomRightRadius: isLast ? '999px' : '0',
      }
    }

    const getChangeText = (value) => {
      if (value > 0) {
        return `↑ ${value}`
      }

      if (value < 0) {
        return `↓ ${Math.abs(value)}`
      }

      return '–'
    }

    const getChangeClass = (value) => {
      if (value > 0) {
        return 'positive-change'
      }

      if (value < 0) {
        return 'negative-change'
      }

      return 'neutral-change'
    }

    return {
      orderedGroups,
      orderedBarSegments,
      orderedBarGridStyle,
      hasChartData,
      getBarPartStyle,
      getChangeText,
      getChangeClass,
    }
  },
}
</script>

<style scoped lang="scss">
.flags-detailed-card {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: start;
}

.flags-detailed-card__groups {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.flags-detailed-card__group {
  display: flex;
  flex-direction: column;
}

.flags-detailed-card__group-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(30px, 60px);
  align-items: center;
  column-gap: 12px;
  min-width: 0;
  margin-bottom: 8px;
}

.flags-detailed-card__group-title-wrap {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.flags-detailed-card__group-title {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
  flex: 0 1 auto;
}

.flags-detailed-card__group-title-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flags-detailed-card__group-line {
  height: 1px;
  background: #E5E5E5;
  flex: 1 1 auto;
  min-width: 12px;
}

.flags-detailed-card__mark {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex: 0 0 auto;
}

.flags-detailed-card__group-total {
  min-width: 50px;
  text-align: right;
  color: rgba(0, 0, 0, 0.88);
  white-space: nowrap;
  font-size: 14px;
  line-height: 120%;
}

.flags-detailed-card__group-total--underlined {
  text-decoration: underline;
  cursor: pointer;
}

.flags-detailed-card__group-items {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &.bigger-gap {
    gap: 8px;
  }
}

.flags-detailed-card__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(30px, 40px) minmax(30px, 60px);
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding-left: 22px;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.flags-detailed-card__row-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flags-detailed-card__row-change {
  min-width: 0;
  white-space: nowrap;
  text-align: right;
}

.flags-detailed-card__row-value {
  min-width: 50px;
  white-space: nowrap;
  text-align: right;
  color: rgba(0, 0, 0, 0.88);
}

.flags-detailed-card__row-value--underlined {
  text-decoration: underline;
  cursor: pointer;
}

.flags-detailed-card__bar-block {
  position: relative;
  margin-top: auto;
}

.flags-detailed-card__bar {
  display: grid;
  width: 100%;
  height: 8px;
  margin-top: 12px;
  gap: 2px;
}

.flags-detailed-card__bar-part {
  height: 100%;
  min-width: 0;
}

.flags-detailed-card__bar-values {
  display: grid;
  width: 100%;
  margin-top: 6px;
  gap: 2px;
  font-size: 13px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.45);
}

.flags-detailed-card__bar-value {
  min-width: 0;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flags-detailed-card__empty {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;

  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  background: #fff;
  font-weight: 400;
  font-size: 12px;
  line-height: 110%;
  text-align: center;
  white-space: nowrap;
}

.positive-change {
  color: var(--main-green);
}

.negative-change {
  color: #F55565;
}

.neutral-change {
  color: rgba(0, 0, 0, 0.45);
}
</style>
